import { BaseTerminalService } from './BaseTerminalService';

// Freedom POS Integration Service
class FreedomPosService extends BaseTerminalService {
    constructor() {
        super();
        this.baseUrl = null;
        this.isInitialized = false;
        this.deviceInfo = null;
        this.needsRegistration = false;
        this.initializationPromise = null;
        this.isDeviceAuthorized = false;
    }

    // Проверка авторизации устройства
    checkDeviceAuthorization() {
        try {
            const deviceRegisterInfo = localStorage.getItem('device_register_info');
            if (!deviceRegisterInfo) {
                console.log('Freedom POS: Device not registered yet');
                this.needsRegistration = true;
                return false;
            }

            const deviceInfo = JSON.parse(deviceRegisterInfo);
            if (!deviceInfo || !deviceInfo.cash_register) {
                console.log('Freedom POS: Device info incomplete');
                return false;
            }

            this.isDeviceAuthorized = true;
            return true;
        } catch (error) {
            console.error('Freedom POS: Error checking device authorization:', error);
            return false;
        }
    }

    // Инициализация сервиса
    async init() {
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        if (!this.checkDeviceAuthorization()) {
            console.log('Freedom POS: Device not authorized, skipping initialization');
            return false;
        }

        this.initializationPromise = this._performInit();
        return this.initializationPromise;
    }

    async _performInit() {
        try {
            console.log('Freedom POS: Starting initialization...');

            const deviceInfo = await this.getLocalDeviceInfo();
            if (!deviceInfo || !deviceInfo.cash_register) {
                console.warn('Freedom POS: Device info or cash_register not found');
                this.needsRegistration = true;
                return false;
            }

            this.deviceInfo = deviceInfo;

            const freedomConfig = this.getFreedomConfig(deviceInfo);
            if (!freedomConfig) {
                console.warn('Freedom POS: Freedom configuration not found');
                this.needsRegistration = true;
                return false;
            }

            this.baseUrl = `http://${freedomConfig.pos_ip_address}:8080`;
            this.isInitialized = true;
            console.log(`Freedom POS: Service initialized successfully with URL: ${this.baseUrl}`);
            return true;
        } catch (error) {
            console.error('Freedom POS: Initialization error:', error);
            this.initializationPromise = null;
            return false;
        }
    }

    // Получение информации об устройстве
    async getLocalDeviceInfo() {
        try {
            const { deviceRegisterService } = await import('./posApi');
            const deviceInfo = await deviceRegisterService.getRegisterInfo();
            return deviceInfo;
        } catch (error) {
            console.error('Freedom POS: Error getting device info from API:', error);
            try {
                const deviceRegisterInfo = localStorage.getItem('device_register_info');
                if (!deviceRegisterInfo) return null;
                return JSON.parse(deviceRegisterInfo);
            } catch (storageError) {
                console.error('Freedom POS: Error parsing device info from localStorage:', storageError);
                return null;
            }
        }
    }

    // Получение конфигурации Freedom
    getFreedomConfig(deviceInfo) {
        return this.getTerminalConfig(deviceInfo, 'freedom');
    }

    // Создание конфигурации Freedom POS
    createFreedomConfig(posIpAddress) {
        try {
            if (!this.deviceInfo) {
                throw new Error('Device info not available');
            }

            const freedomConfig = {
                pos_type: 'freedom',
                pos_ip_address: posIpAddress,
                enabled: true,
                created_at: new Date().toISOString()
            };

            const updatedDeviceInfo = {
                ...this.deviceInfo,
                payment_methods: [
                    ...(this.deviceInfo.payment_methods || []).filter(method => method.pos_type !== 'freedom'),
                    freedomConfig
                ]
            };

            localStorage.setItem('device_register_info', JSON.stringify(updatedDeviceInfo));
            this.deviceInfo = updatedDeviceInfo;

            console.log('Freedom POS: Configuration created successfully');
            return freedomConfig;
        } catch (error) {
            console.error('Freedom POS: Error creating Freedom config:', error);
            return null;
        }
    }

    // Fetch с таймаутом
    async fetchWithTimeout(url, options = {}, timeout = 120000) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return response;
        } catch (error) {
            clearTimeout(timeoutId);

            if (error.name === 'AbortError') {
                throw new Error('Превышен таймаут запроса (120 секунд)');
            }

            throw error;
        }
    }

    // Выполнение запроса
    async makeAuthenticatedRequest(url, options = {}) {
        if (!this.isInitialized) {
            const initialized = await this.init();
            if (!initialized) {
                throw new Error('Freedom POS service not initialized - device not authorized');
            }
        }

        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };

        return this.fetchWithTimeout(url, {
            ...options,
            headers
        });
    }

    // Начало оплаты
    async startPayment(amount, ownCheque = false) {
        try {
            const requestBody = {
                task: 'purchase',
                data: {
                    amount: amount.toString()
                }
            };

            const response = await this.makeAuthenticatedRequest(this.baseUrl, {
                method: 'POST',
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (data.data && data.data.result === 0) {
                // Успешная оплата
                const processId = `freedom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                return {
                    success: true,
                    processId: processId,
                    status: 'success',
                    ...data.data
                };
            } else {
                // Ошибка оплаты
                return {
                    success: false,
                    status: 'fail',
                    error: data.data?.msg || 'Payment failed',
                    processId: `freedom_failed_${Date.now()}`
                };
            }
        } catch (error) {
            return {
                success: false,
                status: 'fail',
                error: error.message,
                processId: `freedom_error_${Date.now()}`
            };
        }
    }

    // Начало возврата
    async startRefund(amount, transactionData, ownCheque = false) {
        try {
            if (!transactionData) {
                throw new Error('Отсутствуют данные оригинальной транзакции');
            }

            const requestBody = {
                task: 'refund',
                data: {
                    amount: amount.toString() // Amount обязателен для Freedom
                }
            };

            // Определяем тип транзакции по channel
            const channel = parseInt(transactionData.channel);

            if (channel === 2) {
                // Jusan QR - используем transactionId
                const transactionId = transactionData.transactionId;
                if (transactionId) {
                    requestBody.data.transactionId = transactionId;
                }
                // Для QR можно не передавать transactionId, тогда будет новый возврат
            } else {
                // Карточная транзакция (channel 0 или 1) - используем RRN
                const rrn = transactionData.tagRRN || transactionData.chequeInfo?.rrn || transactionData.rrn;
                if (rrn) {
                    requestBody.data.tagRRN = rrn;
                }
                // Если RRN нет, будет обычный возврат без привязки
            }

            const response = await this.makeAuthenticatedRequest(this.baseUrl, {
                method: 'POST',
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (data.data && data.data.result === 0) {
                const processId = `freedom_refund_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                return {
                    success: true,
                    processId: processId,
                    status: 'success',
                    ...data.data
                };
            } else {
                return {
                    success: false,
                    status: 'fail',
                    error: data.data?.msg || 'Refund failed',
                    processId: `freedom_refund_failed_${Date.now()}`
                };
            }
        } catch (error) {
            return {
                success: false,
                status: 'fail',
                error: error.message,
                processId: `freedom_refund_error_${Date.now()}`
            };
        }
    }

    // Проверка статуса транзакции (для совместимости с Kaspi)
    async getTransactionStatus(processId) {
        // В Freedom нет отдельного метода для проверки статуса
        // Возвращаем заглушку, так как результат уже получен при startPayment/startRefund
        return {
            success: true,
            status: 'success',
            processId: processId
        };
    }

    // Очистка ресурсов
    destroy() {
        this.initializationPromise = null;
        this.isInitialized = false;
    }

    // Сброс сервиса
    reset() {
        this.destroy();
        this.baseUrl = null;
        this.deviceInfo = null;
        this.needsRegistration = false;
        this.isDeviceAuthorized = false;
    }
}

// Глобальный экземпляр сервиса
const freedomPOSService = new FreedomPosService();

export { freedomPOSService };